import React, { useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import MessagesIcon from "../../../assets/icons/messages.svg2";
import { useGetMessagesReportingWidgetDataQuery } from "../../store/slices/widgetsSlice";
import HelpButton from "../../HelpContent/HelpButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGripVertical, faRotate } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import { AbsoluteLoadingIndicator } from "../FullScreenLoadingIndicator";

const legendFormatterFactory = (totals) =>
  function legendFormatter(value, entry) {
    return (
      <span className="text-white small" style={{ color: entry.color }}>
        {value}: {totals[entry.dataKey]}
      </span>
    );
  };

export default function MessagesWidget() {
  const [period, setPeriod] = useState("last24h");
  const [params, setParams] = useState({
    startDate: "",
    endDate: ",",
    comms: "",
    timeUnit: "hour",
  });
  const {
    data = { data: [] },
    isLoading,
    isFetching,
    refetch,
  } = useGetMessagesReportingWidgetDataQuery(params);
  const loading = isLoading || isFetching;

  const reportData = data.data.map((i) => ({
    time_period: i.time_period,
    sms:
      i.sms.delivered +
      i.sms.sent +
      i.sms.expired +
      i.sms.failed +
      i.sms.pending +
      i.sms.cancelled,
    email:
      i.email.delivered +
      i.email.sent +
      i.email.expired +
      i.email.failed +
      i.email.pending +
      i.email.cancelled,

    voice:
      i.voice.delivered +
      i.voice.sent +
      i.voice.expired +
      i.voice.failed +
      i.voice.pending +
      i.voice.cancelled,
  }));

  const totals = reportData.reduce(
    (acc, curr) => {
      acc.sms += curr.sms || 0;
      acc.email += curr.email || 0;
      acc.voice += curr.voice || 0;
      return acc;
    },
    {
      sms: 0,
      email: 0,
      voice: 0,
    }
  );

  return (
    <div className=" h-100 rounded-3 position-relative">
      <AbsoluteLoadingIndicator loading={loading} />
      <div className="card-body p-3 d-flex flex-column justify-content-between h-100">
        <div className="text-white">
          <MessagesIcon width={20} className=" white opacity-50" />{" "}
          <Link className="btn p-0 text-white" to="/reporting/realtime">
            <h6 className="small fw-bold m-0 mx-2"> MESSAGES</h6>
          </Link>
          <button
            className="btn p-0 text-white ms-2"
            onClick={() => refetch()}
            disabled={loading}
            title="Refresh messages by comms"
          >
            <FontAwesomeIcon
              icon={faRotate}
              className={`opacity-50 ${loading ? "spin" : ""}`}
            />
          </button>
          <HelpButton
            helpKey="widget-messages-by-comms-type"
            iconClassName="white"
          />
          <FontAwesomeIcon
            icon={faGripVertical}
            className="ms-2 opacity-50 widget-header"
          />
          <span className="text-xs ms-2 fw-bold"> by comms channel</span>
          <select
            aria-label="Default select example"
            className="border-0 p-2 py-1 rounded-pill text-purple text-xs fw-bold mx-2 bg-white "
            value={params.comms}
            onChange={(e) => {
              setParams((p) => ({ ...p, comms: e.target.value }));
            }}
          >
            <option value="">SHOW ALL</option>
            <option value="sms">SMS</option>
            <option value="email">EMAIL</option>
            <option value="voice">VOICE</option>
          </select>
          <hr className="my-2" />
        </div>

        <ResponsiveContainer className="flex-grow-1 flex-shrink-1">
          <LineChart
            width={600}
            height={300}
            data={reportData}
            margin={{
              top: 5,
              right: 0,
              left: 0,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              height={15}
              dataKey="time_period"
              tick={{ stroke: "white", fontSize: "0.6rem" }}
            />
            <YAxis width={30} tick={{ stroke: "white", fontSize: "0.6rem" }} />
            <Tooltip
              contentStyle={{
                background: "rgba(0, 0, 0, 0.6)",
                border: "none",
                fontSize: "0.6rem",
              }}
            />
            <Legend
              verticalAlign="top"
              iconSize={10}
              iconType="line"
              formatter={legendFormatterFactory(totals)}
            />
            {(params.comms === "sms" || params.comms === "") && (
              <Line
                type="monotone"
                dataKey="sms"
                stroke="#ff7133"
                activeDot={{ r: 8 }}
              />
            )}
            {(params.comms === "email" || params.comms === "") && (
              <Line
                type="monotone"
                dataKey="email"
                stroke="#33b2ff"
                activeDot={{ r: 8 }}
              />
            )}{" "}
            {(params.comms === "voice" || params.comms === "") && (
              <Line
                type="monotone"
                dataKey="voice"
                stroke="#33ff8a"
                activeDot={{ r: 8 }}
              />
            )}
          </LineChart>
        </ResponsiveContainer>

        <div className="text-white text-center">
          <hr className="my-2" />
          <button
            type="button"
            className={`btn btn-light btn-sm px-3 py-0 rounded-pill text-purple text-xs fw-bold mx-1 ${
              period === "last24h" ? "" : "opacity-50"
            }`}
            onClick={() => {
              setPeriod("last24h");
              setParams({
                startDate: new Date(
                  new Date().setDate(new Date().getDate() - 1)
                ).toISOString(),
                endDate: new Date().toISOString(),
                timeUnit: "hour",
                comms: params.comms,
              });
            }}
          >
            last 24 h
          </button>
          <button
            type="button"
            className={`btn btn-light btn-sm px-3 py-0 rounded-pill text-purple text-xs fw-bold mx-1 ${
              period === "last7days" ? "" : "opacity-50"
            }`}
            onClick={() => {
              setPeriod("last7days");
              setParams({
                startDate: new Date(
                  new Date().setDate(new Date().getDate() - 7)
                ).toISOString(),
                endDate: new Date().toISOString(),
                timeUnit: "day",
                comms: params.comms,
              });
            }}
          >
            last 7 days
          </button>{" "}
          <button
            type="button"
            className={`btn btn-light btn-sm px-3 py-0 rounded-pill text-purple text-xs fw-bold mx-1 ${
              period === "last4weeks" ? "" : "opacity-50"
            }`}
            onClick={() => {
              setPeriod("last4weeks");
              setParams({
                startDate: new Date(
                  new Date().setDate(new Date().getDate() - 28)
                ).toISOString(),
                endDate: new Date().toISOString(),
                timeUnit: "week",
                comms: params.comms,
              });
            }}
          >
            last 4 weeks
          </button>
          <button
            type="button"
            className={`btn btn-light btn-sm px-3 py-0 rounded-pill text-purple text-xs fw-bold mx-1 ${
              period === "last3months" ? "" : "opacity-50"
            }`}
            onClick={() => {
              setPeriod("last3months");
              setParams({
                startDate: new Date(
                  new Date().setMonth(new Date().getMonth() - 3)
                ).toISOString(),
                endDate: new Date().toISOString(),
                timeUnit: "month",
                comms: params.comms,
              });
            }}
          >
            last 3 months
          </button>
        </div>
      </div>
    </div>
  );
}
